/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import DibotChat from '@/components/content/DibotChat';
import { useDibotReactOverride } from '@/components/content/DibotChat/useDibotReactOverride';
import GlobalLoadingIndicator from '@/components/content/Global/GlobalLoadingIndicator';
import { PreviewCommunication } from '@/components/content/PreviewCommunication';
import { AppContextProviders } from '@/data/AppContextProviders';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { NEW_PREVIEW_SESSION_PARAM, PREVIEW_TOKEN_PARAM } from '@/data/constants/preview';
import { LoadingProvider } from '@/data/context/loadingContext';
import { EMPTY_SETTINGS, SettingProvider } from '@/data/context/setting';
import { CookiesProvider } from '@/data/cookie/cookiesProvider';
import { useGTMErrorHandler } from '@/data/events/handlers/gtm/useGTMErrorHandler';
import {
	clearCurrentUserFromStorage,
	CurrentUser,
	saveCurrentUserToStorage,
	savePreviewToken,
} from '@/data/forReactAndMobile/SaveCurrentUser';
import { Settings } from '@/data/Settings';
import { StateProvider } from '@/data/state/provider';
import { MyAppProps } from '@/data/types/PagesRouter';
import { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';
import StorageSync from '@/pages/StorageSync';
import { createEmotionCache } from '@/utils/createEmotionCache';
import { CacheProvider } from '@emotion/react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { NextRouter, useRouter } from 'next/router';
import Script, { ScriptProps } from 'next/script';
import 'polyfill';
import { usePreviousUrl } from 'presentation/events/hooks/usePreviousUrl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Cookies } from 'react-cookie';
import {
	ALLOWED_URL_PATTERNS,
	ENABLE_DIBOT_REACT_FLAG,
} from 'shared/custom/constants/commonConstants';
import { getCookie } from 'shared/custom/utils/cookieHandler';
import { isScriptAllowedForPath } from 'shared/custom/utils/scriptPathFilter';
import 'swiper/css/bundle';
import { useSWRConfig } from 'swr';

// Code-split out @react-google-maps/api: it's only needed for store-locator /
// map features, not for every page, so it shouldn't sit in the main _app chunk.
const GoogleMapsAPILoader = dynamic(
	() => import('@/components/content/GoogleMapsAPILoader').then((mod) => mod.GoogleMapsAPILoader),
	{ ssr: false }
);

const CONSTRUCTOR_BEACON_NOT_ALLOWED_PATH_LIST = 'CONSTRUCTOR_BEACON_NOT_ALLOWED_PATH_LIST';

const ENABLE_ALGOLIA_SCRIPT = 'ENABLE_ALGOLIA_SCRIPT';
const ALGOLIA_SCRIPT_SRC = 'ALGOLIA_SCRIPT_SRC';

// Paths where the React DiBot widget must NOT be mounted globally.
const DIBOT_EXCLUDED_PATHS = [
	/^\/checkout(?:\/.*)?$/, // checkout flow (avoids overlapping the sticky payment bar)
	/^\/cart(?:\/.*)?$/, // cart
	/^\/account(?:\/.*)?$/, // account pages
	/\/dibot-chat\/?$/, // dedicated full-screen host page
];

/**
 * Determines if a click event on a link should be ignored for custom routing.
 * It checks for external links, special click modifiers (like Ctrl-click), and non-primary mouse buttons.
 */
const shouldIgnoreLinkClick = (event: MouseEvent, href: string | null): boolean =>
	!href ||
	href.startsWith('http') ||
	href.startsWith('//') ||
	href.startsWith('#') ||
	event.metaKey ||
	event.ctrlKey ||
	event.shiftKey ||
	event.altKey ||
	event.button !== 0;

export const RouteGuard = ({ router }: { router: NextRouter }) => {
	useEffect(() => {
		// Don't do anything in development
		if (process.env.NODE_ENV === 'development') return;

		if (typeof window === 'undefined') return;
		const isAllowed = (url: string) => {
			if (!url) return false;
			try {
				const path = new URL(url, window.location.origin).pathname;
				return ALLOWED_URL_PATTERNS.some((pattern) => pattern.test(path));
			} catch {
				return false;
			}
		};
		// Case 1: Intercept <a> clicks before Next.js router runs
		const handleLinkClick = (event: MouseEvent) => {
			const link = (event.target as HTMLElement).closest('a');
			if (!link) return;
			const href = link.getAttribute('href') ?? null;
			if (link.getAttribute('target') === '_blank') return;
			if (shouldIgnoreLinkClick(event, href)) return;

			if (href && !isAllowed(href)) {
				event.preventDefault();
				window.location.assign(href);
			}
		};

		// Case 2: Intercept route changes initiated by Next.js router
		const handleRouteChangeStart = (url: string) => {
			if (!isAllowed(url)) {
				window.location.assign(url);
				// abort the route change to prevent the flicker
				throw new Error('ABORT_NAVIGATION');
			}
		};
		// Add event listeners
		document.addEventListener('click', handleLinkClick, true);
		router.events.on('routeChangeStart', handleRouteChangeStart);
		return () => {
			document.removeEventListener('click', handleLinkClick, true);
			router.events.off('routeChangeStart', handleRouteChangeStart);
		};
	}, [router]);
	return null;
};

/**
 * HACK:
 *
 * This issue does not occur during normal production usage.
 * However, when a production page is loaded via Google Tag Assistant,
 * Emotion’s `speedy` mode can cause CSS classes to not be present in the DOM,
 * resulting in incorrect rendering.
 *
 * As a workaround for this Tag Assistant–specific scenario, we detect the
 * debug query parameter and disable `speedy` mode.
 */
const useClientEmotionCache = () => {
	const searchParams = useSearchParams();

	const getGtmDebugFromSessionStorage = () => {
		if (typeof window === 'undefined') {
			return null;
		}

		const value = sessionStorage.getItem('gtm_debug');

		if (typeof value === 'string') {
			return value === 'true';
		}

		return null;
	};

	const saveGtmDebugToSessionStorage = (value: boolean) => {
		if (typeof window === 'undefined') {
			return;
		}

		sessionStorage.setItem('gtm_debug', value ? 'true' : 'false');
	};

	// If this query param is present, the page is most likely loaded from Tag Assistant
	const gtmDebugFromSearchParams = searchParams?.get('gtm_debug');
	const gtmDebugFromSessionStorage = getGtmDebugFromSessionStorage();

	let hasGtmDebug = false;

	if (gtmDebugFromSessionStorage === null) {
		saveGtmDebugToSessionStorage(!!gtmDebugFromSearchParams);
		hasGtmDebug = !!gtmDebugFromSearchParams;
	} else {
		hasGtmDebug = gtmDebugFromSessionStorage;
	}

	const [clientEmotionCache] = useState(
		createEmotionCache({
			// Disable `speedy` mode when running under Tag Assistant
			speedy: process.env.NODE_ENV === 'production' ? !hasGtmDebug : false,
		})
	);

	return clientEmotionCache;
};
const MyApp = ({
	Component,
	pageProps,
	// serverEmotionCache is undefined on the client
	emotionCache: serverEmotionCache,
	cookies,
}: MyAppProps) => {
	const clientEmotionCache = useClientEmotionCache();
	const searchParams = useSearchParams();
	const router = useRouter();

	useGTMErrorHandler();
	const [settings, setSettings] = useState<Settings>(() => pageProps.settings);
	const { mutate } = useSWRConfig();
	const isMobileApp = settings?.isMobileApp;

	const userData = settings?.userData;
	const currentPathName = router.asPath.split('?')[0].split('#')[0];
	const isAlgoliaScriptEnabled = userData?.[ENABLE_ALGOLIA_SCRIPT] === 'true';
	const algoliaScriptSrc = userData?.[ALGOLIA_SCRIPT_SRC];
	const enableAlgoliaScript =
		isAlgoliaScriptEnabled &&
		!!algoliaScriptSrc &&
		isScriptAllowedForPath(userData, currentPathName, CONSTRUCTOR_BEACON_NOT_ALLOWED_PATH_LIST);
	// React DiBot widget gating. The storeconf flag enables it; a session-scoped
	// ?dibotReact=1 override lets internal testers force it in any environment.
	// Not mounted globally on the native app (which uses the /dibot-chat host
	// page) nor on any DIBOT_EXCLUDED_PATHS (checkout, cart, account, host page).
	const dibotReactOverride = useDibotReactOverride();
	const dibotFlagEnabled = dibotReactOverride ?? userData?.[ENABLE_DIBOT_REACT_FLAG] === 'true';
	const showDibotChat =
		dibotFlagEnabled &&
		!isMobileApp &&
		!DIBOT_EXCLUDED_PATHS.some((path) => path.test(currentPathName));
	// Race condition fix for Mobile App Webviews:
	// We sync cookies to Storage immediately during the render phase.
	// useEffect runs AFTER the child component (the page) has rendered and started its own fetches.
	// By moving saveCurrentUserToStorage here, we ensure the token is available in storage
	// BEFORE the child component triggers its initial API requests.
	const hasSyncedMobileStorage = useRef(false);
	if (isMobileApp && typeof window !== 'undefined' && !hasSyncedMobileStorage.current) {
		const WCToken = getCookie('WCToken');
		if (WCToken) {
			const payloadMobile = {
				WCToken,
				WCTrustedToken: getCookie('WCTrustedToken'),
				isGuest: getCookie('isGuest') === 'true',
				userLoggedIn: getCookie('userLoggedIn') === 'true',
				personalizationID: getCookie('personalizationID'),
				resourceName: getCookie('resourceName'),
				userId: getCookie('userId'),
			} as CurrentUser;
			saveCurrentUserToStorage(
				process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '',
				payloadMobile
			);
			hasSyncedMobileStorage.current = true;
		}
	}

	usePreviousUrl();
	useEffect(() => {
		// This code is to insert token in session storage when the params exist and keep it in react pages
		// This token is only for CMC purpose
		const previewTokenSearchParam = searchParams?.get(PREVIEW_TOKEN_PARAM);
		if (previewTokenSearchParam) {
			savePreviewToken(
				process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '',
				previewTokenSearchParam
			);
			const newPreviewSession = searchParams?.get(NEW_PREVIEW_SESSION_PARAM) || 'true';
			if ('true' === newPreviewSession) {
				clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');
			}
		}
	}, [searchParams]);
	useEffect(() => {
		setSettings((prevSettings) => prevSettings ?? pageProps.settings);
	}, [pageProps, settings]);

	const _cookies = useMemo(
		() => (typeof document !== 'undefined' ? new Cookies(document.cookie) : cookies),
		[cookies]
	);
	useEffect(() => {
		if (isMobileApp && typeof window !== 'undefined') {
			const WCToken = getCookie('WCToken');
			if (WCToken) {
				setTimeout(() => {
					mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
					mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
				}, 1000);
			}
		}
	}, [isMobileApp, mutate]);
	return (
		<SettingProvider value={settings ?? EMPTY_SETTINGS}>
			{settings?.csrSession ? (
				<Script {...({} as ScriptProps)} src="/iframeResizer.contentWindow.min.js" />
			) : null}
			{enableAlgoliaScript ? (
				<Script
					id="algolia-script"
					strategy="afterInteractive"
					type="text/javascript"
					src={algoliaScriptSrc}
				/>
			) : null}
			<CookiesProvider cookies={_cookies}>
				<StorageSync />
				<StateProvider>
					{settings?.inPreview ? <PreviewCommunication /> : null}
					<GoogleMapsAPILoader />
					<CacheProvider value={serverEmotionCache ?? clientEmotionCache}>
						<AppContextProviders>
							<LoadingProvider>
								<GlobalLoadingIndicator />
								{/* <RouteGuard router={router} /> */}
								<Component {...pageProps} />
								{showDibotChat ? <DibotChat /> : null}
							</LoadingProvider>
						</AppContextProviders>
					</CacheProvider>
				</StateProvider>
			</CookiesProvider>
		</SettingProvider>
	);
};

export default MyApp;
